import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchGameLineups } from "@/lib/edgeFunctions";
import { useEffect, useRef } from "react";

export interface LineupPlayer {
  player_id?: number;
  number: number;
  name: string;
  pos: string;
  grid?: string | null;
  photo?: string | null;
  nationality?: string | null;
  nationality_iso?: string | null;
  team_api_id?: number | null;
  team_logo?: string | null;
  team_name?: string | null;
}

export interface GameLineups {
  fixture_id: number;
  home_formation: string;
  home_coach_name: string;
  home_coach_photo?: string;
  home_startxi: LineupPlayer[];
  home_substitutes: LineupPlayer[];
  away_formation: string;
  away_coach_name: string;
  away_coach_photo?: string;
  away_startxi: LineupPlayer[];
  away_substitutes: LineupPlayer[];
}

// Helper to check if lineups have nationality data
function hasNationalities(lineups: GameLineups | null): boolean {
  if (!lineups) return false;
  
  const allPlayers = [
    ...(lineups.home_startxi || []),
    ...(lineups.home_substitutes || []),
    ...(lineups.away_startxi || []),
    ...(lineups.away_substitutes || [])
  ];
  
  if (allPlayers.length === 0) return false;
  
  // Check if at least 90% of players have nationality
  const playersWithNationality = allPlayers.filter(p => p.nationality_iso != null && p.nationality_iso !== '').length;
  const percentage = playersWithNationality / allPlayers.length;
  
  return percentage >= 0.9; // At least 90% must have nationality
}

export function useGameLineups(fixtureId: number | null) {
  const hasTriggeredUpdate = useRef(false);
  
  const query = useQuery({
    queryKey: ['game-lineups', fixtureId],
    queryFn: async (): Promise<GameLineups | null> => {
      if (!fixtureId) return null;

      console.log(`[useGameLineups] Fetching lineups for fixture ${fixtureId}`);
      
      // Try to get from database first
      const { data, error } = await supabase
        .from('game_lineups')
        .select('*')
        .eq('fixture_id', fixtureId)
        .maybeSingle();

      if (error) {
        console.error('[useGameLineups] Error fetching lineups:', error);
        throw error;
      }

      // If lineups exist, return them
      if (data) {
        return {
          fixture_id: data.fixture_id,
          home_formation: data.home_formation || '',
          home_coach_name: data.home_coach_name || '',
          home_coach_photo: data.home_coach_photo,
          home_startxi: (data.home_startxi as unknown as LineupPlayer[]) || [],
          home_substitutes: (data.home_substitutes as unknown as LineupPlayer[]) || [],
          away_formation: data.away_formation || '',
          away_coach_name: data.away_coach_name || '',
          away_coach_photo: data.away_coach_photo,
          away_startxi: (data.away_startxi as unknown as LineupPlayer[]) || [],
          away_substitutes: (data.away_substitutes as unknown as LineupPlayer[]) || []
        };
      }

      // If not in database, try to fetch from API
      try {
        console.log(`[useGameLineups] Lineups not in database, fetching from API for fixture ${fixtureId}...`);
        await fetchGameLineups(fixtureId);
        
        // Retry database query after fetch
        const { data: newData, error: retryError } = await supabase
          .from('game_lineups')
          .select('*')
          .eq('fixture_id', fixtureId)
          .maybeSingle();

        if (retryError) throw retryError;
        
        if (newData) {
          return {
            fixture_id: newData.fixture_id,
            home_formation: newData.home_formation || '',
            home_coach_name: newData.home_coach_name || '',
            home_coach_photo: newData.home_coach_photo,
            home_startxi: (newData.home_startxi as unknown as LineupPlayer[]) || [],
            home_substitutes: (newData.home_substitutes as unknown as LineupPlayer[]) || [],
            away_formation: newData.away_formation || '',
            away_coach_name: newData.away_coach_name || '',
            away_coach_photo: newData.away_coach_photo,
            away_startxi: (newData.away_startxi as unknown as LineupPlayer[]) || [],
            away_substitutes: (newData.away_substitutes as unknown as LineupPlayer[]) || []
          };
        }
      } catch (fetchError) {
        console.error('[useGameLineups] Error auto-fetching lineups:', fetchError);
        // Return null instead of throwing to show "not available" state
      }

      return null;
    },
    enabled: !!fixtureId,
    staleTime: 1000 * 60 * 60, // 1 hour - lineups don't change once available
    retry: false
  });

  // Background update for old lineups without nationalities
  useEffect(() => {
    if (query.data && !hasNationalities(query.data) && !hasTriggeredUpdate.current && fixtureId) {
      hasTriggeredUpdate.current = true;
      console.log(`🔄 [useGameLineups] Lineups for fixture ${fixtureId} missing nationalities, triggering retries...`);
      
      // Immediate retry
      fetchGameLineups(fixtureId)
        .then(() => {
          console.log(`✅ [useGameLineups] Immediate retry completed for fixture ${fixtureId}`);
          query.refetch();
        })
        .catch(err => {
          console.error('❌ [useGameLineups] Immediate retry failed:', err);
        });
      
      // Retry after 15 minutes
      setTimeout(() => {
        console.log(`🔄 [useGameLineups] 15-minute retry for fixture ${fixtureId}...`);
        fetchGameLineups(fixtureId)
          .then(() => query.refetch())
          .catch(err => console.error('❌ 15-minute retry failed:', err));
      }, 15 * 60 * 1000);
      
      // Final retry after 1 hour
      setTimeout(() => {
        console.log(`🔄 [useGameLineups] Final 1-hour retry for fixture ${fixtureId}...`);
        fetchGameLineups(fixtureId)
          .then(() => query.refetch())
          .catch(err => console.error('❌ Final retry failed:', err));
      }, 60 * 60 * 1000);
    }
  }, [query.data, fixtureId, query]);

  return query;
}
